
import React, { useEffect, useState, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BarChart3, Users, BookOpen, Award, RefreshCw } from 'lucide-react';
import { useSupabaseAuth } from '@/hooks/auth/supabaseAuth';
import { getAllCoursesUnified as getAllCourses } from '@/utils/allCourses';
import { useDashboardStatsOptimized } from '@/hooks/useDashboardStatsOptimized';
import { useUnifiedCourseCounts } from '@/hooks/useUnifiedCourseCounts';
import { CourseCountBadge } from '@/components/common/CourseCountBadge';
import DashboardStatsSkeleton from '@/components/dashboard/DashboardStatsSkeleton';
import { useRealDashboardStats } from '@/hooks/useRealDashboardStats';
import RealTimeActivity from '@/components/dashboard/RealTimeActivity';
import { setupAdminUser } from '@/utils/adminSetup';
import { useAdminAutoEnrollment } from '@/hooks/useAdminAutoEnrollment';
import { useCourseCompletionWatcher } from '@/hooks/useCourseCompletionWatcher';
import { CertificateProgressWidget } from '@/components/dashboard/CertificateProgressWidget';
import { useCertificateNotifications } from '@/hooks/useCertificateNotifications';
import RoleDebugPanel from '@/components/debug/RoleDebugPanel';
import { useLoginPreload } from '@/hooks/useLoginPreload';
import { SSGStatusWidget } from '@/components/preload/SSGStatusWidget';
import { PreloadErrorBoundary } from '@/components/preload/PreloadErrorBoundary';
import { FEATURES } from '@/config/features';
import { PerformanceWidget } from '@/components/debug/PerformanceWidget';

// Deferred widgets component to improve perceived performance
const DeferredWidgets: React.FC<{ userRole: string | null }> = ({ userRole }) => {
  const [showWidgets, setShowWidgets] = useState(false);

  useEffect(() => {
    // Defer non-critical widgets by 2 seconds
    const timer = setTimeout(() => {
      setShowWidgets(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (!showWidgets) {
    return (
      <div className="grid grid-cols-1 gap-6 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-muted rounded w-1/4"></div>
              <div className="h-8 bg-muted rounded w-3/4"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-6 mb-6">
        <RealTimeActivity />
      </div>

      {/* Performance Widget for admins */}
      {userRole === 'admin' && (
        <PerformanceWidget />
      )}
    </>
  );
};

const Dashboard: React.FC = () => {
  const { user, userRole, isLoading, forceRefreshAuth } = useSupabaseAuth();
  const navigate = useNavigate();
  const allCourses = getAllCourses();
  
  // Non-blocking role validation - refresh in background if needed
  useEffect(() => {
    if (!user) return;
    
    const hasRefreshedRole = sessionStorage.getItem(`role_refreshed_${user.id}`);
    
    if (!hasRefreshedRole) {
      console.log('🔄 Dashboard mounted - refreshing role in background');
      sessionStorage.setItem(`role_refreshed_${user.id}`, 'true');
      
      // Refresh in background without blocking UI
      setTimeout(() => {
        forceRefreshAuth();
      }, 500);
    }
  }, [user?.id]);
  
  // Set up real-time subscriptions for dashboard updates
  useDashboardStatsOptimized();
  
  // Get real dashboard stats
  const {
    overallProgress,
    certificateCount,
    learningStreak,
    loading: statsLoading
  } = useRealDashboardStats();
  
  const { totalCourses: unifiedTotalCourses } = useUnifiedCourseCounts();
  const { isEnrolling } = useAdminAutoEnrollment();
  
  // Phase 8: Trigger login-level preloading
  const { isPreloading } = useLoginPreload(user?.id);
  
  const totalCourses = unifiedTotalCourses;
  
  // Watch for course completions across all enrolled courses
  useCourseCompletionWatcher(undefined); // Pass undefined to watch all courses

  // Check for certificate eligibility across all courses
  useCertificateNotifications(); // Check all enrolled courses

  // Background admin setup (non-blocking)
  useEffect(() => {
    if (user?.email === 'iam_bal@hotmail.com' && userRole === 'admin') {
      const setupKey = `admin_setup_${user.id}`;
      const hasRunSetup = sessionStorage.getItem(setupKey);
      
      if (!hasRunSetup) {
        console.log('Running background admin setup...');
        sessionStorage.setItem(setupKey, 'true');
        // Run in background without blocking UI
        setTimeout(() => {
          setupAdminUser().then(result => {
            console.log('✅ Admin setup result:', result);
            if (result.success) {
              console.log(`📚 Admin enrolled in ${result.assignedCourses} total courses`);
              // Trigger a refresh of enrollment data
              window.dispatchEvent(new CustomEvent('admin-enrollment-complete'));
            }
          }).catch(error => {
            console.error('❌ Admin setup failed:', error);
            sessionStorage.removeItem(setupKey);
          });
        }, 100);
      }
    }
  }, [user?.email, userRole]);

  // Listen for enrollment completion to refresh data
  useEffect(() => {
    const RELOAD_KEY = 'admin_enrollment_reloaded';
    
    const handleEnrollmentComplete = () => {
      console.log('🔄 Admin enrollment complete event received');
      
      // Guard against multiple reloads
      if (sessionStorage.getItem(RELOAD_KEY)) {
        console.log('⚠️ Reload already triggered, skipping');
        return;
      }
      
      sessionStorage.setItem(RELOAD_KEY, 'true');
      console.log('🔄 Reloading page after admin enrollment');
      
      // Reload once
      setTimeout(() => {
        window.location.reload();
      }, 500);
    };

    window.addEventListener('admin-enrollment-complete', handleEnrollmentComplete);
    return () => window.removeEventListener('admin-enrollment-complete', handleEnrollmentComplete);
  }, []);

  // Auth loading with timeout
  const [authStuck, setAuthStuck] = useState(false);
  
  useEffect(() => {
    if (!isLoading) {
      setAuthStuck(false);
      return;
    }
    
    const timer = setTimeout(() => {
      console.warn('⏱️ Dashboard auth loading timeout (8s)');
      setAuthStuck(true);
    }, 8000);
    
    return () => clearTimeout(timer);
  }, [isLoading]);

  if (isLoading) {
    if (authStuck) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background p-4">
          <div className="max-w-md w-full space-y-6 text-center">
            <div className="space-y-3">
              <div className="text-5xl">⏱️</div>
              <h2 className="text-2xl font-bold">Taking Longer Than Expected</h2>
              <p className="text-muted-foreground">
                Dashboard is still loading. This might be due to a slow connection.
              </p>
            </div>
            
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  setAuthStuck(false);
                  forceRefreshAuth();
                }}
                variant="default"
                className="w-full"
                size="lg"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
              <Button 
                onClick={() => window.location.href = '/courses'}
                variant="outline"
                className="w-full"
              >
                Go to Courses
              </Button>
            </div>
          </div>
        </div>
      );
    }
    
    return <DashboardStatsSkeleton />;
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Welcome back! Here's an overview of your learning progress.
          </p>
          {isEnrolling && (
            <div className="mt-2 text-sm text-primary">
              🔄 Setting up course enrollments...
            </div>
          )}
          <div className="mt-3">
            <CourseCountBadge showIcons={true} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="flex items-center space-x-2">
            <span className="capitalize">{userRole || 'User'}</span>
          </Badge>
          <button
            onClick={() => {
              console.log('🔄 Manual role refresh requested');
              forceRefreshAuth();
            }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
            title="Refresh role from database"
          >
            Refresh Role
          </button>
        </div>
      </div>

      {/* Staff-Only Assignment Banner for Regular Users */}
      {userRole === 'user' && (
        <Card className="mb-6 border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">Staff-Managed Learning</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Your courses are carefully assigned by your manager to align with your role and career development goals. 
                  Want to explore additional courses? Contact your manager to discuss your learning path.
                </p>
                <p className="text-xs text-muted-foreground">
                  💡 This ensures focused, relevant learning that supports both your growth and organizational objectives.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {statsLoading ? (
        <DashboardStatsSkeleton />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card 
                  className="cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105"
                  onClick={() => {
                    sessionStorage.setItem('courseNavigationOrigin', 'courses');
                    navigate('/courses');
                  }}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Courses</CardTitle>
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{unifiedTotalCourses}</div>
                    <p className="text-xs text-muted-foreground">
                      {userRole === 'user' ? 'Courses assigned to you' : 'Available courses'}
                    </p>
                  </CardContent>
                </Card>

                <Card 
                  className="cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105"
                  onClick={() => navigate('/progress')}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Progress</CardTitle>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{overallProgress}%</div>
                    <p className="text-xs text-muted-foreground">Overall completion</p>
                  </CardContent>
                </Card>

                <Card 
                  className="cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105"
                  onClick={() => navigate('/certificates')}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Certificates</CardTitle>
                    <Award className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{certificateCount}</div>
                    <p className="text-xs text-muted-foreground">Earned certificates</p>
                  </CardContent>
                </Card>

                <Card 
                  className="cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105"
                  onClick={() => navigate('/profile')}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Streak</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{learningStreak}</div>
                    <p className="text-xs text-muted-foreground">Days learning</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div>
              <SSGStatusWidget />
            </div>
          </div>
        </>
      )}

      <DeferredWidgets userRole={userRole} />

      {/* Role Debug Panel (visible only in development or for debugging) */}
      {(process.env.NODE_ENV === 'development' || window.location.search.includes('debug=true')) && (
        <div className="mt-6">
          <RoleDebugPanel />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
